import is from '@sindresorhus/is';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import { ApplicationContext } from '~/shared/context/context';
import type { RealtimeEventListener } from '~/shared/realtime-event-listener/realtime-event-listener';

const RECONNECTION_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
const HIDDEN_DISCONNECT_DELAY_MS = 30 * 1000;

const ApiFamilyDataSchema = z.object({
    jobArchitectureId: z.string(),
    jsaFamilyId: z.union([z.string(), z.null()]),
    type: z.string().startsWith('JOB_FAMILY'),
});

const ApiLevelDataSchema = z.object({
    jobArchitectureId: z.string(),
    jsaClusterId: z.string(),
    jsaLevelId: z.union([z.string(), z.null()]),
    jsaRoleId: z.string(),
    type: z.string().startsWith('JOB_LEVEL'),
});

const ApiClusterDataSchema = z.object({
    jobArchitectureId: z.string(),
    jsaClusterId: z.union([z.string(), z.null()]),
    jsaFamilyId: z.string(),
    type: z.string().startsWith('JOB_CLUSTER'),
});

const ApiRoleDataSchema = z.object({
    jobArchitectureId: z.string(),
    jsaClusterId: z.string(),
    jsaFamilyId: z.string(),
    jsaRoleId: z.union([z.string(), z.null()]),
    type: z.string().startsWith('JOB_ROLE'),
});

const ApiGenerationStartedDataSchema = z.object({
    jobArchitectureId: z.string(),
    type: z.literal('JOB_FAMILIES_AND_CLUSTERS_GENERATION_STARTED'),
});

const ApiGenerationCompletedDataSchema = z.object({
    jobArchitectureId: z.string(),
    type: z.literal('JOB_FAMILIES_AND_CLUSTERS_GENERATION_COMPLETED'),
});

const ApiClusterReasoningDataSchema = z.object({
    jobArchitectureId: z.string(),
    jsaClusterId: z.string(),
    reasoning: z.string().optional(),
    type: z.literal('JOB_CLUSTER_REASONING'),
});

const ApiFamilyGenerationCompletedDataSchema = z.object({
    jobArchitectureId: z.string(),
    jsaFamilyId: z.string(),
    type: z.literal('JOB_FAMILY_GENERATION_COMPLETED'),
});

const ApiWorkTaskGenerationDataSchema = z.object({
    jobArchitectureId: z.string(),
    jobArchitectureItemId: z.string(),
    jsaLevelId: z.union([z.string(), z.null()]).optional(),
    jsaRoleId: z.union([z.string(), z.null()]).optional(),
    type: z.string().startsWith('WORK_TASK_GENERATION'),
    workTaskId: z.string().optional(),
});

const UnknownDataSchema = z.object({
    jobArchitectureId: z.string().optional(),
    type: z.string(),
});

const ApiJSAAllTasksFinishedDataSchema = z.object({
    jobArchitectureId: z.string(),
    type: z.literal('JSA_ALL_TASKS_FINISHED'),
});

const ApiHistoryUpdatedDataSchema = z.object({
    jobArchitectureId: z.string(),
    type: z.literal('HISTORY_UPDATED'),
});

const ApiJobProfileCommentCreatedDataSchema = z.object({
    commentId: z.string().nullable(),
    jobArchitectureId: z.string(),
    jobArchitectureItemId: z.string(),
    jobArchitectureItemVersion: z.number().int(),
    type: z.literal('JOB_PROFILE_COMMENT_CREATED'),
});

const ApiJsaExportDataSchema = z.object({
    jobArchitectureId: z.string(),
    type: z.enum(['JSA_EXPORT_COMPLETED', 'JSA_EXPORT_FAILED']),
    userId: z.string().nullish(),
});

const ApiSchema = z.object({
    message: z.union([
        ApiClusterDataSchema,
        ApiClusterReasoningDataSchema,
        ApiFamilyDataSchema,
        ApiFamilyGenerationCompletedDataSchema,
        ApiGenerationCompletedDataSchema,
        ApiGenerationStartedDataSchema,
        ApiHistoryUpdatedDataSchema,
        ApiJobProfileCommentCreatedDataSchema,
        ApiJsaExportDataSchema,
        ApiLevelDataSchema,
        ApiRoleDataSchema,
        ApiWorkTaskGenerationDataSchema,
        UnknownDataSchema,
    ]),
});

export type ApiFamilyData = z.infer<typeof ApiFamilyDataSchema>;

export type ApiLevelData = z.infer<typeof ApiLevelDataSchema>;

export type ApiClusterData = z.infer<typeof ApiClusterDataSchema>;

export type ApiRoleData = z.infer<typeof ApiRoleDataSchema>;

export type ApiGenerationStartedData = z.infer<typeof ApiGenerationStartedDataSchema>;

export type ApiGenerationCompletedData = z.infer<typeof ApiGenerationCompletedDataSchema>;

export type ApiClusterReasoningData = z.infer<typeof ApiClusterReasoningDataSchema>;

export type ApiFamilyGenerationCompletedData = z.infer<typeof ApiFamilyGenerationCompletedDataSchema>;

export type ApiWorkTaskGenerationData = z.infer<typeof ApiWorkTaskGenerationDataSchema>;

export type UnknownData = z.infer<typeof UnknownDataSchema>;

export type ApiJSAAllTasksFinishedData = z.infer<typeof ApiJSAAllTasksFinishedDataSchema>;

export type ApiHistoryUpdatedData = z.infer<typeof ApiHistoryUpdatedDataSchema>;

export type ApiJobProfileCommentCreatedData = z.infer<typeof ApiJobProfileCommentCreatedDataSchema>;

export type ApiJsaExportData = z.infer<typeof ApiJsaExportDataSchema>;

export type ApiData =
    | ApiClusterData
    | ApiClusterReasoningData
    | ApiFamilyData
    | ApiFamilyGenerationCompletedData
    | ApiGenerationCompletedData
    | ApiGenerationStartedData
    | ApiHistoryUpdatedData
    | ApiJobProfileCommentCreatedData
    | ApiJSAAllTasksFinishedData
    | ApiJsaExportData
    | ApiLevelData
    | ApiRoleData
    | ApiWorkTaskGenerationData
    | UnknownData;

const isDocumentVisible = (): boolean => document.visibilityState === 'visible';

/**
 * Every open tab would otherwise hold its own stream and refetch on every event, so a hidden tab
 * disconnects after a grace period. Missed updates are refetched by `refetchOnWindowFocus`.
 */
const useIsVisibleWithGracePeriod = (): boolean => {
    const [isVisible, setIsVisible] = useState(isDocumentVisible);

    useEffect(() => {
        let hideTimer: ReturnType<typeof setTimeout> | null = null;

        const clearHideTimer = (): void => {
            if (hideTimer !== null) {
                clearTimeout(hideTimer);
                hideTimer = null;
            }
        };

        const onVisibilityChange = (): void => {
            clearHideTimer();
            if (isDocumentVisible()) {
                setIsVisible(true);
                return;
            }
            hideTimer = setTimeout(() => {
                setIsVisible(false);
            }, HIDDEN_DISCONNECT_DELAY_MS);
        };

        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            clearHideTimer();
        };
    }, []);

    return isVisible;
};

export const useJsaStatus = ({
    jobArchitectureId,
    onMessage,
    paused = false,
}: {
    readonly jobArchitectureId: string | null;
    readonly onMessage: (message: ApiData) => void;
    readonly paused?: boolean;
}): { readonly isConnected: boolean } => {
    const { realtimeEventListenerBuilder, logger } = useContext(ApplicationContext);
    const [isConnected, setIsConnected] = useState(false);
    const sse = useRef<RealtimeEventListener | null>(null);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const pausedRef = useRef(paused);
    const isVisible = useIsVisibleWithGracePeriod();

    pausedRef.current = paused;

    const runSSE = useCallback(() => {
        if (is.nullOrUndefined(jobArchitectureId)) {
            return;
        }

        sse.current?.stop();

        const url = `/v1/events/jsa/${jobArchitectureId}?lookBackSeconds=5` as const;
        sse.current = realtimeEventListenerBuilder.build({ autoReconnect: true, method: 'GET' as const, path: url });

        sse.current.addEventListener('notification', (event: MessageEvent) => {
            let json: unknown;
            try {
                json = JSON.parse(event.data);
            } catch {
                logger.error('[useJsaStatus] Failed to parse SSE event as JSON', event.data);
                return;
            }

            const parsedData = ApiSchema.safeParse(json);

            if (!parsedData.success) {
                logger.warn('[useJsaStatus] SSE event did not match any known schema', {
                    errors: parsedData.error.issues,
                    raw: json,
                });
                return;
            }

            if (pausedRef.current) {
                return;
            }

            onMessage(parsedData.data.message);
        });

        sse.current.addEventListener('close', () => {
            setIsConnected(false);
        });

        sse.current.addEventListener('open', () => {
            setIsConnected(true);
        });

        sse.current.start();
    }, [jobArchitectureId, logger, onMessage, realtimeEventListenerBuilder]);

    const startTimer = useCallback(() => {
        if (timerRef.current !== null) {
            clearInterval(timerRef.current);
        }

        timerRef.current = setInterval(() => {
            runSSE();
        }, RECONNECTION_INTERVAL_MS);
    }, [runSSE]);

    useEffect(() => {
        if (!isVisible) {
            setIsConnected(false);
            return;
        }

        runSSE();
        startTimer();

        return () => {
            sse.current?.stop();
            sse.current = null;
            if (timerRef.current !== null) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [isVisible, runSSE, startTimer]);

    return {
        isConnected,
    };
};
